import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Crop, Check, RotateCw, EyeOff, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve image URLs - handles relative paths, absolute URLs, and legacy domain-specific URLs
function resolveImageUrl(url) {
  if (!url) return '';
  // Already a data URL or absolute with current domain
  if (url.startsWith('data:')) return url;
  // Relative URL - prepend current backend
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  // Absolute URL with any domain - extract relative path and use current backend
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/api/uploads/')) {
      return `${BACKEND_URL}${parsed.pathname}`;
    }
  } catch (e) {}
  return url;
}

export default function ImageUpload({ 
  value, 
  onChange, 
  folder = 'general',
  label = 'Upload Image',
  accept = 'image/*',
  maxSize = 10, // MB
  className = '',
  previewSize = 'md', // 'sm', 'md', 'lg'
  enableCrop = true, // Enable cropping feature
  aspectRatio = undefined, // e.g., 16/9, 1, 4/3
  maxWidth = null, // Server-side max width
  maxHeight = null, // Server-side max height
  quality = 85 // JPEG quality
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  
  // Cropping state
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);

  // Blur tool state
  const [showBlurDialog, setShowBlurDialog] = useState(false);
  const [blurImageSrc, setBlurImageSrc] = useState(null);
  const [blurFile, setBlurFile] = useState(null);
  const [blurRegions, setBlurRegions] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [blurStrength, setBlurStrength] = useState(20);
  const blurCanvasRef = useRef(null);
  const blurImgRef = useRef(null);

  const previewSizes = {
    sm: 'h-16 w-16',
    md: 'h-24 w-24',
    lg: 'h-32 w-32'
  };

  // Generate cropped image blob
  const getCroppedImg = useCallback(async () => {
    if (!imgRef.current || !completedCrop) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
    canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

    ctx.scale(pixelRatio, pixelRatio);
    ctx.imageSmoothingQuality = 'high';

    const cropX = completedCrop.x * scaleX;
    const cropY = completedCrop.y * scaleY;
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    // Handle rotation
    const centerX = canvas.width / 2 / pixelRatio;
    const centerY = canvas.height / 2 / pixelRatio;

    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate((rotate * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.translate(-centerX, -centerY);

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    ctx.restore();

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], selectedFile?.name || 'cropped.jpg', { type: 'image/jpeg' }));
          } else {
            resolve(null);
          }
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop, rotate, scale, selectedFile]);

  const uploadFile = async (file) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      if (maxWidth) formData.append('max_width', maxWidth.toString());
      if (maxHeight) formData.append('max_height', maxHeight.toString());
      if (quality) formData.append('quality', quality.toString());

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      // Store relative URL for portability across environments
      const relativeUrl = response.data.url.startsWith('/') 
        ? response.data.url
        : response.data.url;
      
      setImageError(false);
      onChange(relativeUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`File too large. Maximum size is ${maxSize}MB`);
      return;
    }

    if (enableCrop) {
      // Show crop dialog
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setShowCropDialog(true);
        setCrop(undefined);
        setCompletedCrop(null);
        setScale(1);
        setRotate(0);
      };
      reader.readAsDataURL(file);
    } else {
      // Upload directly without cropping
      await uploadFile(file);
    }
  };

  const handleCropComplete = async () => {
    let fileToProcess;
    if (completedCrop && completedCrop.width > 0 && completedCrop.height > 0) {
      fileToProcess = await getCroppedImg();
    } else {
      fileToProcess = selectedFile;
    }
    if (fileToProcess) {
      setShowCropDialog(false);
      openBlurDialog(fileToProcess);
    }
  };

  const handleSkipCrop = async () => {
    setShowCropDialog(false);
    if (selectedFile) {
      openBlurDialog(selectedFile);
    }
  };

  // Blur dialog functions
  const openBlurDialog = (file) => {
    setBlurFile(file);
    setBlurRegions([]);
    setCurrentRect(null);
    const reader = new FileReader();
    reader.onload = () => {
      setBlurImageSrc(reader.result);
      setShowBlurDialog(true);
    };
    reader.readAsDataURL(file);
  };

  const getCanvasCoords = (e) => {
    const canvas = blurCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const handleBlurMouseDown = (e) => {
    const coords = getCanvasCoords(e);
    if (coords) {
      setIsDrawing(true);
      setDrawStart(coords);
      setCurrentRect(null);
    }
  };

  const handleBlurMouseMove = (e) => {
    if (!isDrawing || !drawStart) return;
    const coords = getCanvasCoords(e);
    if (coords) {
      setCurrentRect({
        x: Math.min(drawStart.x, coords.x),
        y: Math.min(drawStart.y, coords.y),
        width: Math.abs(coords.x - drawStart.x),
        height: Math.abs(coords.y - drawStart.y)
      });
    }
  };

  const handleBlurMouseUp = () => {
    if (isDrawing && currentRect && currentRect.width > 5 && currentRect.height > 5) {
      setBlurRegions(prev => [...prev, currentRect]);
    }
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
  };

  const undoLastBlur = () => {
    setBlurRegions(prev => prev.slice(0, -1));
  };

  const applyBlurAndUpload = async () => {
    const img = blurImgRef.current;
    if (!img) return;

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    // Apply blur to each region using iterative box blur
    for (const region of blurRegions) {
      const scaleX = img.naturalWidth / (blurCanvasRef.current?.width || img.naturalWidth);
      const scaleY = img.naturalHeight / (blurCanvasRef.current?.height || img.naturalHeight);
      const rx = Math.round(region.x * scaleX);
      const ry = Math.round(region.y * scaleY);
      const rw = Math.round(region.width * scaleX);
      const rh = Math.round(region.height * scaleY);

      if (rw <= 0 || rh <= 0) continue;

      // Use OffscreenCanvas or temp canvas for blur
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = rw;
      tempCanvas.height = rh;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(canvas, rx, ry, rw, rh, 0, 0, rw, rh);

      // Pixelate-blur: scale down then up
      const pixelSize = Math.max(2, Math.round(blurStrength / 2));
      const smallW = Math.max(1, Math.round(rw / pixelSize));
      const smallH = Math.max(1, Math.round(rh / pixelSize));
      
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.drawImage(tempCanvas, 0, 0, rw, rh, 0, 0, smallW, smallH);
      tempCtx.imageSmoothingEnabled = true;
      tempCtx.drawImage(tempCanvas, 0, 0, smallW, smallH, 0, 0, rw, rh);

      ctx.drawImage(tempCanvas, 0, 0, rw, rh, rx, ry, rw, rh);
    }

    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], blurFile?.name || 'edited.jpg', { type: 'image/jpeg' });
        setShowBlurDialog(false);
        await uploadFile(file);
      }
    }, 'image/jpeg', 0.92);
  };

  const skipBlurAndUpload = async () => {
    setShowBlurDialog(false);
    if (blurFile) {
      await uploadFile(blurFile);
    }
  };

  // Draw blur overlay preview on canvas
  const drawBlurPreview = useCallback(() => {
    const canvas = blurCanvasRef.current;
    const img = blurImgRef.current;
    if (!canvas || !img || !img.complete) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw semi-transparent overlay on blur regions
    [...blurRegions, ...(currentRect ? [currentRect] : [])].forEach(region => {
      ctx.fillStyle = 'rgba(0, 100, 255, 0.25)';
      ctx.fillRect(region.x, region.y, region.width, region.height);
      ctx.strokeStyle = 'rgba(0, 150, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(region.x, region.y, region.width, region.height);
      ctx.setLineDash([]);
      
      // Label
      ctx.fillStyle = 'rgba(0, 100, 255, 0.7)';
      ctx.fillRect(region.x, region.y, 40, 18);
      ctx.fillStyle = '#fff';
      ctx.font = '11px sans-serif';
      ctx.fillText('BLUR', region.x + 4, region.y + 13);
    });
  }, [blurRegions, currentRect]);

  // Redraw canvas when regions change
  const blurImgOnLoad = () => {
    drawBlurPreview();
  };

  useEffect(() => {
    drawBlurPreview();
  }, [blurRegions, currentRect, drawBlurPreview]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleRemove = () => {
    setImageError(false);
    onChange('');
  };

  const onImageLoad = (e) => {
    imgRef.current = e.currentTarget;
    
    // Set initial crop based on aspect ratio
    if (aspectRatio) {
      const { width, height } = e.currentTarget;
      const cropWidth = Math.min(width, height * aspectRatio);
      const cropHeight = cropWidth / aspectRatio;
      
      setCrop({
        unit: 'px',
        x: (width - cropWidth) / 2,
        y: (height - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight
      });
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
      />

      {value ? (
        <div className="flex items-start gap-3">
          <div className={`${previewSizes[previewSize]} rounded-lg overflow-hidden bg-muted/20 border border-glass-border flex items-center justify-center`}>
            {imageError ? (
              <span className="text-xs text-muted-foreground">Error</span>
            ) : (
              <img 
                src={resolveImageUrl(value)} 
                alt="Preview" 
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              type="button"
              variant="outline" 
              size="sm" 
              onClick={handleClick}
              disabled={uploading}
              className="border-glass-border"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {uploadProgress}%
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Replace
                </>
              )}
            </Button>
            <Button 
              type="button"
              variant="ghost" 
              size="sm" 
              onClick={handleRemove}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <X className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${dragOver 
              ? 'border-neon-blue bg-neon-blue/10' 
              : 'border-glass-border hover:border-muted-foreground'
            }
          `}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 text-neon-blue animate-spin" />
              <div className="w-full max-w-xs">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-muted-foreground mt-2">Uploading... {uploadProgress}%</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-full bg-muted/20 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">
                  Drag & drop or click to select (max {maxSize}MB)
                </p>
                {enableCrop && (
                  <p className="text-xs text-neon-blue mt-1">
                    <Crop className="w-3 h-3 inline mr-1" />
                    Crop & resize available
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Crop Dialog */}
      <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
        <DialogContent className="max-w-3xl bg-obsidian border-glass-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Crop className="w-5 h-5 text-neon-blue" />
              Crop & Resize Image
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Image Preview with Crop */}
            <div className="flex justify-center bg-black/50 rounded-lg p-4 max-h-[400px] overflow-auto">
              {imageSrc && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspectRatio}
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt="Crop preview"
                    style={{
                      transform: `scale(${scale}) rotate(${rotate}deg)`,
                      maxHeight: '350px',
                      maxWidth: '100%'
                    }}
                    onLoad={onImageLoad}
                  />
                </ReactCrop>
              )}
            </div>

            {/* Controls */}
            <div className="grid grid-cols-2 gap-6">
              {/* Scale */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center justify-between">
                  <span>Scale</span>
                  <span className="text-foreground">{Math.round(scale * 100)}%</span>
                </label>
                <Slider
                  value={[scale * 100]}
                  onValueChange={([v]) => setScale(v / 100)}
                  min={50}
                  max={200}
                  step={5}
                  className="w-full"
                />
              </div>

              {/* Rotate */}
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center justify-between">
                  <span>Rotate</span>
                  <span className="text-foreground">{rotate}°</span>
                </label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[rotate]}
                    onValueChange={([v]) => setRotate(v)}
                    min={-180}
                    max={180}
                    step={1}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setRotate((r) => (r + 90) % 360)}
                    className="border-glass-border h-8 w-8"
                  >
                    <RotateCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Tips */}
            <p className="text-xs text-muted-foreground text-center">
              Drag to select crop area. {aspectRatio ? `Aspect ratio: ${aspectRatio}:1` : 'Free-form crop available.'}
            </p>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowCropDialog(false)}
              className="text-muted-foreground"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleSkipCrop}
              className="border-glass-border"
            >
              Skip Crop
            </Button>
            <Button
              type="button"
              onClick={handleCropComplete}
              className="bg-neon-blue hover:bg-neon-blue/80"
            >
              <Check className="w-4 h-4 mr-2" />
              Next
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blur Dialog */}
      <Dialog open={showBlurDialog} onOpenChange={(open) => { if (!open) { setShowBlurDialog(false); } }}>
        <DialogContent className="max-w-4xl bg-obsidian border-glass-border" data-testid="blur-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <EyeOff className="w-5 h-5 text-amber-400" />
              Blur Sensitive Areas
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Draw rectangles over areas you want to blur. Click and drag to select regions.
            </p>

            {/* Image with canvas overlay */}
            <div className="relative flex justify-center bg-black/50 rounded-lg p-2 overflow-auto max-h-[450px]">
              {blurImageSrc && (
                <div className="relative inline-block">
                  <img
                    ref={blurImgRef}
                    src={blurImageSrc}
                    alt="Blur preview"
                    onLoad={blurImgOnLoad}
                    className="block max-h-[420px] max-w-full"
                    draggable={false}
                  />
                  <canvas
                    ref={blurCanvasRef}
                    className="absolute inset-0 w-full h-full cursor-crosshair"
                    onMouseDown={handleBlurMouseDown}
                    onMouseMove={handleBlurMouseMove}
                    onMouseUp={handleBlurMouseUp}
                    onMouseLeave={handleBlurMouseUp}
                    data-testid="blur-canvas"
                  />
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4">
              <div className="flex-1 space-y-1">
                <label className="text-sm text-muted-foreground flex items-center justify-between">
                  <span>Blur Strength</span>
                  <span className="text-foreground">{blurStrength}</span>
                </label>
                <Slider
                  value={[blurStrength]}
                  onValueChange={([v]) => setBlurStrength(v)}
                  min={5}
                  max={50}
                  step={1}
                  className="w-full"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={undoLastBlur}
                disabled={blurRegions.length === 0}
                className="border-glass-border"
              >
                <Undo2 className="w-4 h-4 mr-1" />
                Undo
              </Button>
              <span className="text-xs text-muted-foreground">
                {blurRegions.length} region{blurRegions.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowBlurDialog(false)}
              className="text-muted-foreground"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={skipBlurAndUpload}
              className="border-glass-border"
            >
              Skip Blur
            </Button>
            <Button
              type="button"
              onClick={applyBlurAndUpload}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="apply-blur-btn"
            >
              {blurRegions.length > 0 ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Apply Blur & Upload
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Upload Without Blur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
